import React from 'react';
import { Box, Button, Tooltip } from '@mui/material';
import { DateBlock } from '../../types';
import { ReactElement } from 'react';
import { getFormattedDate, isSameTime } from '../../utils';
import { Dayjs } from 'dayjs';

export type TimeSelectorDayTooltipProps<TFeature> = {
    items: DateBlock<TFeature>[] | undefined;
    selected: boolean;
    open?: boolean;
    onTooltipOpen?: (open: boolean) => void;
    onDateClick?: (date: DateBlock<TFeature>) => void;
    selectedValue?: string | number | Date | Dayjs | null | undefined;
    children: ReactElement;
};
/**
 * Takes in a list of dates for a timespan and creates a tooltip with a clickable button for each date
 */
export function TimeSelectorDayTooltip<TFeature>({
    items,
    selected,
    open,
    onTooltipOpen,
    onDateClick,
    selectedValue,
    children
}: TimeSelectorDayTooltipProps<TFeature>) {
    return items ? (
        <Tooltip
            placement="top"
            disableFocusListener
            enterTouchDelay={0}
            leaveDelay={500}
            open={open}
            onOpen={() => onTooltipOpen?.(true)}
            onClose={() => onTooltipOpen?.(false)}
            title={
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        gap: theme.spacing(0.5),
                        marginY: theme.spacing(0.5)
                    })}
                >
                    {items.map((dateBlock, i) => {
                        const isSelected = isSameTime(selectedValue, dateBlock.date);
                        return (
                            <Button
                                fullWidth
                                variant="text"
                                sx={(theme) => ({
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginX: 0,
                                    padding: theme.spacing(0.5),
                                    fontSize: 13,
                                    backgroundColor: isSelected
                                        ? theme.palette.primary.main
                                        : theme.palette.secondary.main,
                                    color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.primary,
                                    ...(!isSelected && {
                                        '&:hover': {
                                            backgroundColor: theme.palette.action.hover,
                                            color: theme.palette.getContrastText(theme.palette.action.hover)
                                        }
                                    })
                                })}
                                key={i}
                                data-testid={`date-option-tooltip-${dateBlock.id}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDateClick?.(dateBlock);
                                }}
                            >
                                {getFormattedDate(new Date(dateBlock.date))}
                            </Button>
                        );
                    })}
                </Box>
            }
            slotProps={{
                tooltip: {
                    sx: (theme) => ({
                        backgroundColor: theme.palette.secondary.main,
                        border: '2px solid',
                        borderColor: selected
                            ? theme.palette.primary.main
                            : theme.palette.mode === 'dark'
                              ? theme.palette.grey[800]
                              : 'transparent',
                        padding: theme.spacing(0.4),
                        borderRadius: theme.shape.borderRadius,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        overflow: 'hidden',
                        '&&.MuiTooltip-tooltip': {
                            margin: 0
                        },
                        width: `${theme.timeSelector.slideWidth}px`,
                        boxShadow: theme.mixins.tooltipShadow
                    })
                },
                popper: {
                    popperOptions: {
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, selected ? -2 : 0]
                                }
                            },
                            // prevents the tooltip from sliding when scrolling horizontally
                            { name: 'preventOverflow', enabled: false }
                        ]
                    }
                }
            }}
        >
            {children}
        </Tooltip>
    ) : (
        children
    );
}
